import { Map, View, Feature } from 'ol';
import { Style } from 'ol/style';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { defaults as InteractionDefaults } from 'ol/interaction';
import { Point, Polygon, LineString } from 'ol/geom';
import { defaults as ControlDefaults } from 'ol/control';
import { MapStyles } from './MapStyles';
import { IStyleCache, MapLayerGroupType } from '../Models/MapConfigModels';
import { RouteMapStyles } from './MapStyleConfig/RouteMapStyles';
import { TROMapStyles } from './MapStyleConfig/TROMapStyles';

export interface LegendConfig {
    symbologyKey?: string;
    legendName?: string;
    mapLayerGroupType?: MapLayerGroupType;
};

export class MapLegend {
    // ----------------------------------------------------------- //
    // GLOBAL VARS
    private legendContainerTemplate = `
        <li class="d-flex">
            <div idsub style="height: 30px; width: 40px; background-color: transparent; display: block; position: relative;"></div>
            <span style="padding-top: 3px;">NAMESUB</span>
        </li>
    `;

    private legendModalHeaderTemplate = `
        <div class="row mt-3" style="font-size: 1rem; font-weight: 600;">
            <div class="col-sm-3">
                NAMESUB
            </div>
        </div>
    `;

    private legendModalContainerTemplate = `
        <div idsub style="height: 30px; width: 40px; background-color: transparent; display: block; position: relative;"></div>
    `;

    private mapStyles: MapStyles;

    // ----------------------------------------------------------- //
    // Constructor
    public constructor(mapStyles: MapStyles) {
        const self = this;
        self.mapStyles = mapStyles;
        self.initEvents();
    }

    private initEvents = (): void => {
        const self = this;
        
        $('[data-maplayergrouptype] [data-toggle]').on('click', function () {
            var parentEl = $(this).parent().parent();
            var id = $(this).data('target').replace('#', '');
            setTimeout(function () { self.createLegendInPanel(id, parentEl); }, 300);
        });
    }

    // ----------------------------------------------------------- //
    // Public functions
    public createLegendInPanel = (parentId: string, el: JQuery<HTMLElement>): void => {
        const self = this;
        var count = 0;

        var t: MapLayerGroupType = el.data('maplayergrouptype');
        var c = el.find('[name="legendContainer"]');
       
        // Only create the legend if it has no children and hasn't been created before
        if (c.find('li').length == 0) {

            var geomType = "";
            if (t == MapLayerGroupType.MyMaint) {
                geomType = "point";
                self.mapStyles.MaintMapStyles.maintStyleCache.forEach((s,i) => createLegendItem(s));
            }
            else if (t == MapLayerGroupType.Maint) {
                geomType = "point";
                self.mapStyles.MaintMapStyles.maintStyleCache.forEach((s, i) => createLegendItem(s));
            }
            else if (t == MapLayerGroupType.Infra) {
                geomType = "point";
                self.mapStyles.InfraMapStyles.infraStyleCache.forEach((s, i) => createLegendItem(s));
            }
            else if (t == MapLayerGroupType.Route) {
                geomType = "line"; 
                self.mapStyles.RouteMapStyles.routeStyleCache.forEach((s, i) => createLegendItem(s));
            }
            else if (t == MapLayerGroupType.Land) {
                geomType = "polygon";
                self.mapStyles.LandMapStyles.landStyleCache.forEach((s, i) => createLegendItem(s));
            }
            else if (t == MapLayerGroupType.TRO) {
                geomType = "line";
                TROMapStyles.troStyleCache.forEach((s, i) => createLegendItem(s));
            }
        }

        function createLegendItem(s: IStyleCache) {
            // Append the new HTML tag
            var id = parentId + "_legendItem" + count;
            var namesub = window.G_SITEFOLDERNAME == "/cy" ? s.attributeAltLabel : s.attributeLabel;
            var str = self.legendContainerTemplate.replace('idsub', 'id="' + id + '"');
            str = str.replace('NAMESUB', namesub);
            c.append(str);

            // Add the map legend
            //self.addLegendMap(id, s.style, geomType);
            count++;
        }
    }

    public createLegendInModal = (): void => {
        const self = this;
        var count = 0;

        // Just get the unique styles per symbologyKey and attributeValue
        var maintStyles: Array<IStyleCache> = [];
        var infraStyles: Array<IStyleCache> = [];
        var routeStyles: Array<IStyleCache> = [];
        var landStyles: Array<IStyleCache> = [];
        var legalRoutesStyles: Array<IStyleCache> = [];
        var legalFeaturesStyles: Array<IStyleCache> = [];
        self.mapStyles.MaintMapStyles.maintStyleCache.forEach(f => { if (maintStyles.find(x => x.symbologyKey == f.symbologyKey && x.attributeValue == f.attributeValue) == null) maintStyles.push(f); });
        self.mapStyles.InfraMapStyles.infraStyleCache.forEach(f => { if (infraStyles.find(x => x.symbologyKey == f.symbologyKey && x.attributeValue == f.attributeValue) == null) infraStyles.push(f); });
        self.mapStyles.RouteMapStyles.routeStyleCache.forEach(f => { if (routeStyles.find(x => x.symbologyKey == f.symbologyKey && x.attributeValue == f.attributeValue) == null) routeStyles.push(f); });
        self.mapStyles.LandMapStyles.landStyleCache.forEach(f => { if (landStyles.find(x => x.symbologyKey == f.symbologyKey && x.attributeValue == f.attributeValue) == null) landStyles.push(f); });
        self.mapStyles.LegalRouteMapStyles.legalRouteStyleCache.forEach(f => { if (legalRoutesStyles.find(x => x.symbologyKey == f.symbologyKey && x.attributeValue == f.attributeValue) == null) legalRoutesStyles.push(f); });
        self.mapStyles.LegalFeatureMapStyles.legalFeatureStyleCache.forEach(f => { if (legalFeaturesStyles.find(x => x.symbologyKey == f.symbologyKey && x.attributeValue == f.attributeValue) == null) legalFeaturesStyles.push(f); });
        
        // First get a list of all of the map layers that are visible
        var config: Array<LegendConfig> = [];
        var count: number = 0;
        $('#coreLayerSwitcherTarget [data-coremaplayer]').each(function (l) {
            var isVisible = $(this).find('input').prop('checked');
  
            if (isVisible == true) {
                var c: LegendConfig = {};
                count = count + 1;
                c.symbologyKey = $(this).data('symbologykey');
                c.mapLayerGroupType = $(this).data('maplayergrouptype');
                c.legendName = $(this).data('legendname');

                // Only add if the layer is visible and if the symbology key has not been added previously
                if (config.find(x => x.symbologyKey == c.symbologyKey) == null) {
                    config.push(c);
                }
            }
            
        });
        if (count > 0) {
            // Go through each config item and create legend items
            config.forEach(c => {
                // Add layer
                switch (c.mapLayerGroupType) {
                    case MapLayerGroupType.Maint: addMaint(c); break;
                    case MapLayerGroupType.MyMaint: addMaint(c); break;
                    case MapLayerGroupType.Infra: addInfra(c); break;
                    case MapLayerGroupType.Route: addRoute(c); break;
                    case MapLayerGroupType.Land: addLand(c); break;
                    case MapLayerGroupType.LegalRoute: addLegalRoute(c); break;
                    case MapLayerGroupType.LegalFeature: addLegalFeature(c); break;
                    default: break;
                }
            });
        } else {            
            $('#mapLegend_NoFeatures').show();
        }
     
            
       
        // ****************************************************************************************************************** //
        // MAINT
        function addMaint(c: LegendConfig) {
            var tmpEl = createLegendHeader($('#mapLegend_Maint'), c.symbologyKey);
            maintStyles.filter(f => f.symbologyKey == c.symbologyKey).forEach((s) => {
                if (s.showInLegend == true) {
                    // Append the new HTML tag
                    var id = "mapLegendItem" + count;
                    self.createLegendItem(s, tmpEl, id, true, true, true);
                    count++;
                }
            });
        }

        function addInfra(c: LegendConfig) {
            var tmpEl = createLegendHeader($('#mapLegend_Infra'), c.symbologyKey);
            infraStyles.filter(f => f.symbologyKey == c.symbologyKey).forEach((s) => {
                if (s.showInLegend == true) {
                    // Append the new HTML tag
                    var id = "mapLegendItem" + count;
                    self.createLegendItem(s, tmpEl, id, true, true, true);
                    count++;
                }
            });
        }

        function addRoute(c: LegendConfig) {
            var tmpEl = createLegendHeader($('#mapLegend_Route'), c.symbologyKey);
            routeStyles.filter(f => f.symbologyKey == c.symbologyKey).forEach((s) => {
                if (s.showInLegend == true) {
                    // Append the new HTML tag
                    var id = "mapLegendItem" + count;
                    self.createLegendItem(s, tmpEl, id, false, true, false);
                    count++;
                }
            });
        }

        function addLand(c: LegendConfig) {
            var tmpEl = createLegendHeader($('#mapLegend_Land'), c.symbologyKey);
            landStyles.filter(f => f.symbologyKey == c.symbologyKey).forEach((s) => {
                if (s.showInLegend == true) {
                    // Append the new HTML tag
                    var id = "mapLegendItem" + count;
                    self.createLegendItem(s, tmpEl, id, false, false, true);
                    count++;
                }
            });
        }

        function addLegalRoute(c: LegendConfig) {
            var tmpEl = createLegendHeader($('#mapLegend_LegalRoutes'), c.symbologyKey);
            legalRoutesStyles.filter(f => f.symbologyKey == c.symbologyKey).forEach((s) => {
                if (s.showInLegend == true) {
                    // Append the new HTML tag
                    var id = "mapLegendItem" + count;
                    self.createLegendItem(s, tmpEl, id, false, true, false);
                    count++;
                }
            });
        }

        function addLegalFeature(c: LegendConfig) {
            var tmpEl = createLegendHeader($('#mapLegend_LegalFeatures'), c.symbologyKey);
            legalFeaturesStyles.filter(f => f.symbologyKey == c.symbologyKey).forEach((s) => {
                if (s.showInLegend == true) {
                    // Append the new HTML tag
                    var id = "mapLegendItem" + count;
                    self.createLegendItem(s, tmpEl, id, false, true, false);
                    count++;
                }
            });
        }
        
        function createLegendHeader(el: JQuery<HTMLElement>, text: string): JQuery<HTMLElement> {
            var str = self.legendModalHeaderTemplate.replace('NAMESUB', text);
            el.append(str);

            var tmpEl = $("<div>", { "class": "row" });
            el.append(tmpEl);
            return tmpEl
        }    
    }

    // ----------------------------------------------------------- //
    // OpenLayers private methods

    private createLegendItem = (s: IStyleCache, c: JQuery<HTMLElement>, id: string, isPoint: boolean, isLine: boolean, isPoly: boolean): void => {
        var self = this;

        var namesub = window.G_SITEFOLDERNAME == "/cy" ? s.attributeAltLabel : s.attributeLabel;
        var str = `<div class="col-sm-3"><div class="d-flex">`;
        if (isPoint == true && s.style.getImage()!=null) str += self.legendModalContainerTemplate.replace('idsub', 'id="' + id + '_point"');
        if (isLine == true && s.style.getStroke() != null) str += self.legendModalContainerTemplate.replace('idsub', 'id="' + id + '_line"');
        if (isPoly == true && s.style.getFill() != null) str += self.legendModalContainerTemplate.replace('idsub', 'id="' + id + '_poly"');
        str += `<span style="padding-top: 6px; font-size: 0.8rem;">${namesub}</span></div></div>`;
        c.append(str);

        if (isPoint == true && s.style.getImage() != null) createMapIcon(id + '_point', true, false, true);
        if (isLine == true && s.style.getStroke() != null) createMapIcon(id + '_line', false, true, false);
        if (isPoly == true && s.style.getFill() != null) createMapIcon(id + '_poly', false, false, true);

        function createMapIcon(id, isPoint, isLine, isPoly) {
            // Add the map legend
            let newMap = new Map({
                target: id,
                controls: ControlDefaults({
                    attribution: false, rotate: false, zoom: false
                }),
                interactions: InteractionDefaults({
                    altShiftDragRotate: false, dragPan: false, doubleClickZoom: false, keyboard: false, mouseWheelZoom: false, shiftDragZoom: false, pinchZoom: false, pinchRotate: false
                }),
                view: new View({
                    center: [40, 0],
                    zoom: 16
                })
            });

            let f = [];
            if (isPoint == true && s.style.getImage() != null) {
                f.push(new Feature({
                    geometry: new Point([20, 0])
                }));
            }
            else if (isLine == true && s.style.getStroke() != null) {
                f.push(new Feature({
                    geometry: new LineString([[10, 0], [10 + 40, 0]])
                }));
            }
            else if (isPoly == true && s.style.getFill() != null) {
                f.push(new Feature({
                    geometry: new Polygon([[[10, -20], [10, 20], [10 + 40, 20], [10 + 40, -20], [10, -20]]])
                }));
            }

            let vl = new VectorLayer({
                source: new VectorSource({
                    features: f
                }),
                style: s.style
            });

            newMap.addLayer(vl);
        }

        
    }
}




